// Transform editor sheet (right-side drawer).
// Ontology spec §7.1 — opened when clicking a transform in the ObjectTypeEditor.
// Contains: CodeMirror JS editor, trigger toggle, dry-run preview, save bar, history button.
// Part 1: optional CSV upload to guide authoring (parse-and-scaffold).
// Part 2: multi-target Object Type selector (wired to save_transform + run-transform).

import { lazy, Suspense, useEffect, useRef, useState, useCallback } from "react";
import { Play, History, Loader2, Upload, X } from "lucide-react";

const TransformCodeEditor = lazy(() => import("./TransformCodeEditor"));
import { Sheet, SheetContent, SheetHeader, SheetClose } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { Badge } from "@/components/ui/badge";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  useTransform,
  useSaveTransform,
  useRunTransform,
  useTransformTargets,
} from "@/hooks/useTransforms";
import { useObjectTypes, useObjectType } from "@/hooks/useObjectTypes";
import { StaleVersionError } from "@/types/ontology";
import type {
  Transform,
  TransformTriggerMode,
  SaveTransformInput,
  CsvGuide,
} from "@/types/ontology";
import type { RunTransformPreviewRow } from "@/types/edge-functions";
import { GenericHistoryDrawer } from "@/components/history/GenericHistoryDrawer";
import {
  transformVersionAdapter,
  type TransformCurrentState,
} from "@/components/history/transformVersionAdapter";
import { RunHistoryList } from "@/components/ontology/RunHistoryList";
import { parseCSV } from "@/lib/backtest/csvParser";

// ── CSV row cap ───────────────────────────────────────────────────────────────

const CSV_ROW_CAP = 50;
const CSV_PREVIEW_ROWS = 3;

// ── Default code scaffold ─────────────────────────────────────────────────────

const DEFAULT_CODE = `/**
 * @param inputs  Array of source Object instances.
 * @returns       Array of { pk: string, data: object } for the target Object Type.
 */
function transform(inputs) {
  return inputs.map((row) => ({
    pk: row.id,
    data: {
      // populate target properties here
    },
  }));
}`;

/**
 * Returns true if `name` is a valid JS identifier (no bracket notation needed).
 * Accepts only /^[A-Za-z_$][A-Za-z0-9_$]*$/ — the safe subset.
 */
function isJsIdentifier(name: string): boolean {
  return /^[A-Za-z_$][A-Za-z0-9_$]*$/.test(name);
}

/** Build a scaffolded transform body referencing detected CSV column names. */
export function buildCsvScaffoldCode(headers: string[]): string {
  if (headers.length === 0) return DEFAULT_CODE;
  const colLines = headers
    .map((h) => {
      const accessor = isJsIdentifier(h) ? `row.${h}` : `row[${JSON.stringify(h)}]`;
      return `      ${JSON.stringify(h)}: ${accessor},`;
    })
    .join("\n");
  return `/**
 * @param inputs  Array of source Object instances.
 * Detected CSV columns: ${headers.join(", ")}
 * @returns       Array of { pk: string, data: object } for the target Object Type.
 */
function transform(inputs) {
  return inputs.map((row) => ({
    pk: row.id ?? row._pk,
    data: {
${colLines}
    },
  }));
}`;
}

// ── Stale version dialog ───────────────────────────────────────────────────────

interface StaleDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onReload: () => void;
}

function TransformStaleVersionDialog({ open, onOpenChange, onReload }: StaleDialogProps) {
  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>This transform was edited</AlertDialogTitle>
          <AlertDialogDescription>
            Another user saved this transform while you were editing. Reload to see the latest
            version — your unsaved changes will be lost.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel>Cancel</AlertDialogCancel>
          <AlertDialogAction
            className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            onClick={() => {
              onReload();
              onOpenChange(false);
            }}
          >
            Reload — discard my edits
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

// ── CSV guide panel ───────────────────────────────────────────────────────────

interface CsvGuidePanelProps {
  guide: CsvGuide;
  onDismiss: () => void;
}

function CsvGuidePanel({ guide, onDismiss }: CsvGuidePanelProps) {
  return (
    <div className="px-4 py-3 border-b bg-muted/20 space-y-2">
      <div className="flex items-center justify-between">
        <span className="text-xs font-medium text-muted-foreground">
          CSV columns ({guide.headers.length})
        </span>
        <Button variant="ghost" size="icon" className="w-5 h-5" onClick={onDismiss}>
          <X className="w-3 h-3" />
        </Button>
      </div>
      {/* Column chips */}
      <div className="flex flex-wrap gap-1">
        {guide.headers.map((h) => (
          <span
            key={h}
            className="inline-flex items-center rounded px-1.5 py-0.5 text-xs font-mono bg-secondary text-secondary-foreground"
          >
            {h}
          </span>
        ))}
      </div>
      {/* Sample rows */}
      {guide.sampleRows.length > 0 && (
        <div className="overflow-x-auto rounded border border-border">
          <table className="text-xs w-full">
            <thead className="bg-muted/40">
              <tr>
                {guide.headers.slice(0, 6).map((h) => (
                  <th key={h} className="px-2 py-1 text-left font-medium whitespace-nowrap">
                    {h}
                  </th>
                ))}
                {guide.headers.length > 6 && (
                  <th className="px-2 py-1 text-left font-medium text-muted-foreground">
                    +{guide.headers.length - 6} more
                  </th>
                )}
              </tr>
            </thead>
            <tbody>
              {guide.sampleRows.map((row, i) => (
                <tr key={i} className="border-t border-border">
                  {guide.headers.slice(0, 6).map((h) => (
                    <td key={h} className="px-2 py-1 font-mono truncate max-w-[120px]">
                      {row[h] ?? ""}
                    </td>
                  ))}
                  {guide.headers.length > 6 && <td className="px-2 py-1" />}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ── TransformEditorSheet ──────────────────────────────────────────────────────

interface TransformEditorSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** ID of the transform to edit (null → create new). */
  transformId: string | null;
  /** Pre-filled source object type id (used when creating from ObjectTypeEditor). */
  defaultSourceObjectTypeId?: string;
  /** Pre-filled target object type id (used when creating from ObjectTypeEditor). */
  defaultTargetObjectTypeId?: string;
  /** Called after a new transform is created so parent can update selection. */
  onCreated?: (id: string) => void;
}

export function TransformEditorSheet({
  open,
  onOpenChange,
  transformId,
  defaultSourceObjectTypeId,
  defaultTargetObjectTypeId,
  onCreated,
}: TransformEditorSheetProps) {
  const isNew = transformId === null;
  const { transform, isLoading } = useTransform(transformId);
  const saveTransform = useSaveTransform();
  const runTransform = useRunTransform();

  // All Object Types for the multi-target selector.
  const { objectTypes } = useObjectTypes();

  // Source/target labels
  const { objectType: sourceOt } = useObjectType(
    transform?.source_object_type_id ?? defaultSourceObjectTypeId ?? null
  );
  const { objectType: targetOt } = useObjectType(
    transform?.target_object_type_id ?? defaultTargetObjectTypeId ?? null
  );

  // Extra targets stored in the DB (transform_targets join table).
  const { targets: savedExtraTargets } = useTransformTargets(transformId);

  // Editor state
  const [code, setCode] = useState(DEFAULT_CODE);
  const [triggerMode, setTriggerMode] = useState<TransformTriggerMode>("manual");
  const [name, setName] = useState("");
  const [note, setNote] = useState("");
  const [isDirty, setIsDirty] = useState(false);
  const [staleDialogOpen, setStaleDialogOpen] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [showRunHistory, setShowRunHistory] = useState(false);

  // Part 1: CSV guide state
  const [csvGuide, setCsvGuide] = useState<CsvGuide | null>(null);
  const csvInputRef = useRef<HTMLInputElement>(null);

  // Part 2: extra target ids (beyond the primary target_object_type_id).
  const [extraTargetIds, setExtraTargetIds] = useState<string[]>([]);

  // Dry-run state
  const [previewRows, setPreviewRows] = useState<RunTransformPreviewRow[] | null>(null);
  const [previewError, setPreviewError] = useState<string | null>(null);
  const [previewMs, setPreviewMs] = useState<number | null>(null);

  const loadedVersionRef = useRef<number | null>(null);

  // Populate from loaded transform
  useEffect(() => {
    if (!transform) {
      if (isNew) {
        setCode(DEFAULT_CODE);
        setTriggerMode("manual");
        setName("");
        setExtraTargetIds([]);
        loadedVersionRef.current = null;
      }
      return;
    }

    setCode(transform.code || DEFAULT_CODE);
    setTriggerMode(transform.trigger_mode);
    setName(transform.name);
    loadedVersionRef.current = transform.current_version;
    setIsDirty(false);
  }, [transform, isNew]);

  // Sync extra targets from DB when they load.
  useEffect(() => {
    if (savedExtraTargets.length > 0) {
      setExtraTargetIds(savedExtraTargets.map((t) => t.object_type_id));
    }
  }, [savedExtraTargets]);

  function markDirty() {
    setIsDirty(true);
  }

  // ── Part 1: CSV upload ─────────────────────────────────────────────────────

  const handleCsvFile = useCallback(
    (file: File) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        const text = e.target?.result as string;
        const { headers, rows } = parseCSV(text);
        const cappedRows = rows.slice(0, CSV_ROW_CAP);

        const guide: CsvGuide = {
          headers,
          sampleRows: cappedRows.slice(0, CSV_PREVIEW_ROWS),
        };
        setCsvGuide(guide);

        // Only scaffold if it's a new transform or the code is still the default.
        if (headers.length > 0 && (isNew || code === DEFAULT_CODE)) {
          const scaffolded = buildCsvScaffoldCode(headers);
          setCode(scaffolded);
          markDirty();
        }
      };
      reader.readAsText(file);
    },
    [isNew, code]
  );

  function handleCsvInputChange(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (file) handleCsvFile(file);
    // Reset the input so the same file can be re-uploaded if needed.
    e.target.value = "";
  }

  // ── Part 2: multi-target toggle ────────────────────────────────────────────

  function toggleExtraTarget(otId: string) {
    setExtraTargetIds((prev) => {
      const next = prev.includes(otId) ? prev.filter((id) => id !== otId) : [...prev, otId];
      return next;
    });
    markDirty();
  }

  // Ids that cannot be extra-targets: source + primary target.
  const primaryTargetId = transform?.target_object_type_id ?? defaultTargetObjectTypeId ?? "";
  const sourceId = transform?.source_object_type_id ?? defaultSourceObjectTypeId ?? "";

  // ── Save ───────────────────────────────────────────────────────────────────

  async function handleSave() {
    const input: SaveTransformInput = {
      name: name || "Untitled Transform",
      api_name: isNew
        ? (name || "untitled-transform")
            .toLowerCase()
            .replace(/[^a-z0-9]+/g, "-")
            .replace(/^-+|-+$/g, "")
        : undefined,
      source_object_type_id: transform?.source_object_type_id ?? defaultSourceObjectTypeId ?? "",
      target_object_type_id: transform?.target_object_type_id ?? defaultTargetObjectTypeId ?? "",
      code,
      trigger_mode: triggerMode,
    };

    try {
      const result = await saveTransform.mutateAsync({
        id: transformId,
        expectedVersion: loadedVersionRef.current,
        input,
        note: note || null,
        extraTargetIds: extraTargetIds.length > 0 ? extraTargetIds : [],
      });

      loadedVersionRef.current = result.current_version;
      setIsDirty(false);
      setNote("");

      if (isNew && result.id) {
        onCreated?.(result.id);
      }
    } catch (err) {
      if (err instanceof StaleVersionError) {
        setStaleDialogOpen(true);
      }
      // Other errors are toasted by the hook.
    }
  }

  function handleReload() {
    if (!transform) return;
    setCode(transform.code || DEFAULT_CODE);
    setTriggerMode(transform.trigger_mode);
    setName(transform.name);
    loadedVersionRef.current = transform.current_version;
    setIsDirty(false);
  }

  // ── Dry run ────────────────────────────────────────────────────────────────

  async function handleDryRun() {
    if (!transformId) return;
    setPreviewRows(null);
    setPreviewError(null);
    setPreviewMs(null);

    try {
      const result = await runTransform.mutateAsync({
        transform_id: transformId,
        dry_run: true,
      });

      if (result.preview_rows) {
        setPreviewRows(result.preview_rows);
        setPreviewMs(result.execution_time_ms ?? null);
      }
    } catch (err) {
      setPreviewError((err as Error).message ?? "Dry run failed");
    }
  }

  // ── Render ─────────────────────────────────────────────────────────────────

  const currentState: TransformCurrentState = { code };

  const sourceLabel = sourceOt?.name ?? "Source";
  const targetLabel = targetOt?.name ?? "Target";

  // Extra targets as objects for display.
  const extraTargetObjects = objectTypes.filter((ot) => extraTargetIds.includes(ot.id));

  return (
    <>
      <Sheet open={open} onOpenChange={onOpenChange}>
        <SheetContent side="right" className="w-[820px] max-w-full flex flex-col p-0">
          {/* ── Header ──────────────────────────────────────────────────────── */}
          <SheetHeader className="h-14 border-b px-4 flex flex-row items-center justify-between shrink-0">
            <div className="flex items-center gap-3 flex-1 min-w-0">
              {isLoading && !isNew ? (
                <Skeleton className="h-5 w-48" />
              ) : (
                <>
                  <Input
                    value={name}
                    onChange={(e) => {
                      setName(e.target.value);
                      markDirty();
                    }}
                    placeholder="Transform name"
                    className="font-medium text-sm border-0 bg-transparent p-0 h-auto focus-visible:ring-0 w-48"
                  />
                  {isDirty && (
                    <span className="text-xs text-muted-foreground shrink-0">● unsaved</span>
                  )}
                </>
              )}
            </div>

            <div className="flex items-center gap-2 shrink-0">
              <Button
                variant="outline"
                size="sm"
                className="gap-1.5"
                onClick={() => setHistoryOpen(true)}
                disabled={isNew}
              >
                <History className="w-3.5 h-3.5" />
                History
              </Button>
              <Button
                size="sm"
                onClick={handleSave}
                disabled={!isDirty || saveTransform.isPending}
                className="gap-1.5"
              >
                {saveTransform.isPending ? "Saving…" : "Save"}
              </Button>
              <SheetClose asChild>
                <Button variant="ghost" size="icon" className="w-8 h-8">
                  ×
                </Button>
              </SheetClose>
            </div>
          </SheetHeader>

          <div className="flex-1 overflow-y-auto">
            {/* ── Trigger config ─────────────────────────────────────────────── */}
            <div className="px-4 py-3 border-b flex items-center gap-4">
              <span className="text-xs text-muted-foreground font-medium">Trigger</span>
              <div className="flex rounded-md border border-border overflow-hidden">
                <button
                  className={[
                    "px-3 py-1 text-xs transition-colors",
                    triggerMode === "manual"
                      ? "bg-primary text-primary-foreground"
                      : "hover:bg-muted",
                  ].join(" ")}
                  onClick={() => {
                    setTriggerMode("manual");
                    markDirty();
                  }}
                >
                  Manual
                </button>
                <button
                  className={[
                    "px-3 py-1 text-xs transition-colors",
                    triggerMode === "on_upstream_materialize"
                      ? "bg-primary text-primary-foreground"
                      : "hover:bg-muted",
                  ].join(" ")}
                  onClick={() => {
                    setTriggerMode("on_upstream_materialize");
                    markDirty();
                  }}
                >
                  On upstream materialization
                </button>
              </div>

              {triggerMode === "on_upstream_materialize" && sourceOt && (
                <span className="text-xs text-muted-foreground">
                  Runs automatically after every successful materialization of{" "}
                  <em>{sourceOt.name}</em>.
                </span>
              )}
            </div>

            {/* ── Source → Target(s) summary ────────────────────────────────── */}
            {(sourceOt || targetOt) && (
              <div className="px-4 py-2 border-b flex items-center gap-2 text-sm text-muted-foreground flex-wrap">
                <span className="font-medium text-foreground">{sourceLabel}</span>
                <span>→</span>
                <span className="font-medium text-foreground">{targetLabel}</span>
                {extraTargetObjects.map((ot) => (
                  <span key={ot.id} className="flex items-center gap-1">
                    <span className="text-muted-foreground">+</span>
                    <span className="font-medium text-foreground">{ot.name}</span>
                  </span>
                ))}
              </div>
            )}

            {/* ── Part 2: Extra target Object Types ─────────────────────────── */}
            <div className="px-4 py-3 border-b space-y-2">
              <div className="flex items-center justify-between">
                <span className="text-xs font-medium text-muted-foreground">
                  Additional target Object Types
                </span>
                <span className="text-xs text-muted-foreground">
                  Output rows can declare <code className="font-mono text-xs">object_type</code> to
                  route to a specific target
                </span>
              </div>
              <div className="flex flex-wrap gap-1.5">
                {objectTypes
                  .filter((ot) => ot.id !== primaryTargetId && ot.id !== sourceId && !ot.deleted_at)
                  .map((ot) => {
                    const selected = extraTargetIds.includes(ot.id);
                    return (
                      <button
                        key={ot.id}
                        onClick={() => toggleExtraTarget(ot.id)}
                        className={[
                          "px-2 py-0.5 rounded text-xs border transition-colors",
                          selected
                            ? "bg-primary text-primary-foreground border-primary"
                            : "border-border hover:bg-muted",
                        ].join(" ")}
                      >
                        {ot.name}
                      </button>
                    );
                  })}
                {objectTypes.filter(
                  (ot) => ot.id !== primaryTargetId && ot.id !== sourceId && !ot.deleted_at
                ).length === 0 && (
                  <span className="text-xs text-muted-foreground italic">
                    No other Object Types available
                  </span>
                )}
              </div>
            </div>

            {/* ── Note input ─────────────────────────────────────────────────── */}
            <div className="px-4 py-2 border-b">
              <Input
                value={note}
                onChange={(e) => setNote(e.target.value)}
                placeholder="Save note (optional)…"
                className="text-xs"
              />
            </div>

            {/* ── Part 1: CSV upload affordance ──────────────────────────────── */}
            {isNew && (
              <div className="px-4 py-2 border-b flex items-center gap-3">
                <span className="text-xs text-muted-foreground">Guide from CSV:</span>
                <Button
                  variant="outline"
                  size="sm"
                  className="gap-1.5"
                  onClick={() => csvInputRef.current?.click()}
                >
                  <Upload className="w-3.5 h-3.5" />
                  Upload sample CSV
                </Button>
                {csvGuide && (
                  <span className="text-xs text-muted-foreground">
                    {csvGuide.headers.length} columns detected
                  </span>
                )}
                <input
                  ref={csvInputRef}
                  type="file"
                  accept=".csv,text/csv"
                  className="hidden"
                  onChange={handleCsvInputChange}
                />
              </div>
            )}

            {/* ── Part 1: CSV guide panel (columns + sample rows) ────────────── */}
            {csvGuide && <CsvGuidePanel guide={csvGuide} onDismiss={() => setCsvGuide(null)} />}

            {/* ── Code editor ────────────────────────────────────────────────── */}
            <div className="border-b">
              {!isNew && isLoading ? (
                <Skeleton className="h-64 w-full rounded-none" />
              ) : (
                <Suspense fallback={<Skeleton className="h-[320px] w-full rounded-none" />}>
                  <TransformCodeEditor
                    value={code}
                    onChange={(val) => {
                      setCode(val);
                      markDirty();
                    }}
                  />
                </Suspense>
              )}
            </div>

            {/* ── Dry-run preview ────────────────────────────────────────────── */}
            <div className="px-4 py-4 border-b space-y-3">
              <div className="flex items-center justify-between">
                <h4 className="text-sm font-medium">Dry-run preview</h4>
                <Button
                  variant="outline"
                  size="sm"
                  className="gap-1.5"
                  onClick={handleDryRun}
                  disabled={isNew || runTransform.isPending}
                >
                  {runTransform.isPending ? (
                    <Loader2 className="w-3.5 h-3.5 animate-spin" />
                  ) : (
                    <Play className="w-3.5 h-3.5" />
                  )}
                  Run preview on first {10} inputs
                </Button>
              </div>

              {previewError && <p className="text-xs text-destructive">{previewError}</p>}

              {previewRows !== null && previewRows.length === 0 && !previewError && (
                <p className="text-xs text-muted-foreground">No rows returned.</p>
              )}

              {previewRows !== null && previewRows.length > 0 && (
                <div className="space-y-2">
                  {previewMs !== null && (
                    <p className="text-xs text-muted-foreground">Execution time: {previewMs}ms</p>
                  )}
                  <div className="overflow-x-auto rounded border border-border">
                    <table className="text-xs w-full">
                      <thead className="bg-muted/40">
                        <tr>
                          <th className="px-2 py-1 text-left font-medium">pk</th>
                          {Object.keys(previewRows[0]?.data ?? {})
                            .slice(0, 5)
                            .map((k) => (
                              <th key={k} className="px-2 py-1 text-left font-medium">
                                {k}
                              </th>
                            ))}
                        </tr>
                      </thead>
                      <tbody>
                        {previewRows.map((row, i) => (
                          <tr key={i} className="border-t border-border">
                            <td className="px-2 py-1 font-mono">{row.pk}</td>
                            {Object.values(row.data ?? {})
                              .slice(0, 5)
                              .map((v, j) => (
                                <td key={j} className="px-2 py-1 font-mono">
                                  {String(v ?? "")}
                                </td>
                              ))}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>

            {/* ── Run history ────────────────────────────────────────────────── */}
            {transformId && (
              <div className="px-4 py-4 space-y-2">
                <button
                  className="flex items-center gap-2 text-sm font-medium hover:text-primary transition-colors"
                  onClick={() => setShowRunHistory((prev) => !prev)}
                >
                  Run history {showRunHistory ? "▲" : "▼"}
                </button>
                {showRunHistory && <RunHistoryList transformId={transformId} />}
              </div>
            )}
          </div>
        </SheetContent>
      </Sheet>

      {/* ── History drawer ──────────────────────────────────────────────────── */}
      {transformId && (
        <GenericHistoryDrawer
          open={historyOpen}
          onOpenChange={setHistoryOpen}
          entityId={transformId}
          currentVersion={loadedVersionRef.current ?? 0}
          isDirty={isDirty}
          currentState={currentState}
          adapter={transformVersionAdapter}
          title="Transform history"
        />
      )}

      {/* ── Stale version dialog ─────────────────────────────────────────────── */}
      <TransformStaleVersionDialog
        open={staleDialogOpen}
        onOpenChange={setStaleDialogOpen}
        onReload={handleReload}
      />
    </>
  );
}

// ── Helper badge for trigger mode ─────────────────────────────────────────────

export function TriggerModeBadge({ mode }: { mode: TransformTriggerMode }) {
  return (
    <Badge variant={mode === "manual" ? "outline" : "default"} className="text-xs">
      {mode === "manual" ? "Manual" : "Auto"}
    </Badge>
  );
}

// Re-export Transform type for convenience
export type { Transform };
